import React from 'react';
import { View, Text, StyleSheet, ScrollView, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { adminTheme, getColor, getSpacing, getBorderRadius, getShadow } from '../../theme/adminTheme';

const ActivityTimeline = ({ activities = [] }) => {
    // Sample data if none provided
    const defaultActivities = [
        {
            id: 1,
            type: 'user_registered',
            user: 'John Doe',
            action: 'registered as a new participant',
            time: '5 minutes ago',
            icon: 'person-add',
            color: 'success',
        },
        {
            id: 2,
            type: 'inspiration_added',
            user: 'Admin',
            action: 'added a new daily inspiration',
            time: '1 hour ago',
            icon: 'bulb',
            color: 'warning',
        },
        {
            id: 3,
            type: 'participant_updated',
            user: 'Jane Smith',
            action: 'updated their profile',
            time: '2 hours ago',
            icon: 'create',
            color: 'primary',
        },
        {
            id: 4,
            type: 'event_created',
            user: 'Admin',
            action: 'created a new event',
            time: '3 hours ago',
            icon: 'calendar',
            color: 'primary',
        },
        {
            id: 5,
            type: 'user_login',
            user: 'Multiple Users',
            action: '15 users logged in',
            time: '4 hours ago',
            icon: 'log-in',
            color: 'success',
        },
    ];

    const displayActivities = activities.length > 0 ? activities : defaultActivities;

    return (
        <View style={[styles.container, getShadow('md')]}>
            <View style={styles.header}>
                <Text style={styles.title}>Recent Activity</Text>
                <Text style={styles.subtitle}>Latest system events</Text>
            </View>

            <ScrollView
                style={styles.timeline}
                showsVerticalScrollIndicator={false}
            >
                {displayActivities.map((activity, index) => (
                    <View key={activity.id} style={styles.activityItem}>
                        {/* Timeline Line */}
                        {index < displayActivities.length - 1 && (
                            <View style={styles.timelineLine} />
                        )}

                        {/* Icon */}
                        <View style={[
                            styles.iconContainer,
                            { backgroundColor: getColor(`${activity.color}.50`) }
                        ]}>
                            <Ionicons
                                name={activity.icon}
                                size={16}
                                color={getColor(`${activity.color}.600`)}
                            />
                        </View>

                        {/* Content */}
                        <View style={styles.activityContent}>
                            <Text style={styles.activityText}>
                                <Text style={styles.activityUser}>{activity.user}</Text>
                                {' '}
                                <Text style={styles.activityAction}>{activity.action}</Text>
                            </Text>
                            <Text style={styles.activityTime}>{activity.time}</Text>
                        </View>
                    </View>
                ))}
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        backgroundColor: '#FFFFFF',
        borderRadius: getBorderRadius('lg'),
        padding: getSpacing('lg'),
        flex: 1,
        minHeight: 400,
    },
    header: {
        marginBottom: getSpacing('lg'),
        paddingBottom: getSpacing('md'),
        borderBottomWidth: 1,
        borderBottomColor: getColor('gray.200'),
    },
    title: {
        fontSize: 18,
        fontWeight: '600',
        color: getColor('gray.900'),
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    subtitle: {
        fontSize: 14,
        color: getColor('gray.500'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    timeline: {
        flex: 1,
    },
    activityItem: {
        flexDirection: 'row',
        marginBottom: getSpacing('lg'),
        position: 'relative',
    },
    timelineLine: {
        position: 'absolute',
        left: 15,
        top: 32,
        bottom: -getSpacing('lg'),
        width: 2,
        backgroundColor: getColor('gray.200'),
    },
    iconContainer: {
        width: 32,
        height: 32,
        borderRadius: getBorderRadius('full'),
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: getSpacing('md'),
        zIndex: 1,
    },
    activityContent: {
        flex: 1,
        paddingTop: 4,
    },
    activityText: {
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 4,
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
    activityUser: {
        fontWeight: '600',
        color: getColor('gray.900'),
    },
    activityAction: {
        color: getColor('gray.600'),
    },
    activityTime: {
        fontSize: 12,
        color: getColor('gray.500'),
        ...(Platform.OS === 'web' ? { fontFamily: 'Inter, sans-serif' } : {}),
    },
});

export default ActivityTimeline;
